import { Controller } from '@hotwired/stimulus';

/**
 * Controls the review visa mode toggle (draw / upload / saved).
 * Manages which panel is visible and syncs the hidden review_data field.
 */
export default class extends Controller {
  static targets = [
    'modeButton',
    'drawPanel',
    'uploadPanel',
    'savedPanel',
    'uploadInput',
    'uploadPreview',
    'reviewData',
    'saveVisaCheckbox',
  ];
  static values = { savedVisaUrl: String };

  connect() {
    this.savedVisaDataUri = null;

    // Detect initial mode from whichever tab has aria-selected="true"
    const selectedButton = this.modeButtonTargets.find(
      (btn) => btn.getAttribute('aria-selected') === 'true'
    );
    this.currentMode = selectedButton?.dataset.mode || 'draw';

    // Pre-fetch saved visa as data URI if URL is available
    if (this.hasSavedVisaUrlValue && this.savedVisaUrlValue) {
      this.prefetchSavedVisa();
    }
  }

  switchMode(event) {
    event.preventDefault();
    const mode = event.currentTarget.dataset.mode;
    if (mode === this.currentMode) return;

    this.currentMode = mode;
    this.showPanel(mode);
    this.updateButtons(mode);

    // When switching to saved mode, sync the saved visa data
    if (mode === 'saved') {
      this.syncSavedVisa();
    }

    // When switching to draw mode, copy pad's internal value to the form field
    if (mode === 'draw') {
      this.syncDrawFromPad();
    }

    // When switching away from upload, clear preview
    if (mode !== 'upload') {
      this.clearUploadPreview();
    }
  }

  showPanel(mode) {
    if (this.hasDrawPanelTarget) {
      this.drawPanelTarget.classList.toggle('hidden', mode !== 'draw');
    }
    if (this.hasUploadPanelTarget) {
      this.uploadPanelTarget.classList.toggle('hidden', mode !== 'upload');
    }
    if (this.hasSavedPanelTarget) {
      this.savedPanelTarget.classList.toggle('hidden', mode !== 'saved');
    }
    if (this.hasSaveVisaCheckboxTarget) {
      this.saveVisaCheckboxTarget.classList.toggle('hidden', mode === 'saved');
    }
  }

  updateButtons(activeMode) {
    this.modeButtonTargets.forEach((button) => {
      const isActive = button.dataset.mode === activeMode;
      button.classList.toggle('bg-primary-50', isActive);
      button.classList.toggle('text-primary-700', isActive);
      button.classList.toggle('border-primary-200', isActive);
      button.classList.toggle('bg-white', !isActive);
      button.classList.toggle('text-neutral-700', !isActive);
      button.classList.toggle('border-neutral-200', !isActive);
    });
  }

  handleFileUpload(event) {
    const file = event.target.files[0];
    if (!file) return;

    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      this.showUploadError('Format invalide. Utilisez PNG ou JPG.');
      event.target.value = '';
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      this.showUploadError('Le fichier ne doit pas dépasser 2 MB.');
      event.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUri = e.target.result;
      if (this.hasReviewDataTarget) {
        this.reviewDataTarget.value = dataUri;
      }
      this.showUploadPreview(dataUri);
    };
    reader.readAsDataURL(file);
  }

  showUploadPreview(dataUri) {
    if (!this.hasUploadPreviewTarget) return;
    this.uploadPreviewTarget.innerHTML = `<img src="${dataUri}" alt="Aperçu du visa" class="h-16 w-auto object-contain" />`;
    this.uploadPreviewTarget.classList.remove('hidden');
  }

  clearUploadPreview() {
    if (this.hasUploadPreviewTarget) {
      this.uploadPreviewTarget.innerHTML = '';
      this.uploadPreviewTarget.classList.add('hidden');
    }
    if (this.hasUploadInputTarget) {
      this.uploadInputTarget.value = '';
    }
  }

  showUploadError(message) {
    if (!this.hasUploadPreviewTarget) return;
    this.uploadPreviewTarget.innerHTML = `<p class="text-sm text-danger-600">${message}</p>`;
    this.uploadPreviewTarget.classList.remove('hidden');
  }

  // Called via data-action="review-pad:sync->review-mode#syncDrawData"
  syncDrawData(event) {
    if (this.currentMode !== 'draw') return;
    if (!this.hasReviewDataTarget) return;
    this.reviewDataTarget.value = event.detail.data || '';
  }

  // Copy the pad's internal input value to the form hidden field when switching to draw
  syncDrawFromPad() {
    if (!this.hasReviewDataTarget || !this.hasDrawPanelTarget) return;
    const padInput = this.drawPanelTarget.querySelector('[data-review-pad-target="input"]');
    if (padInput) {
      this.reviewDataTarget.value = padInput.value || '';
    }
  }

  async prefetchSavedVisa() {
    try {
      const response = await fetch(this.savedVisaUrlValue);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      this.savedVisaDataUri = await this.blobToDataUri(blob);
    } catch {
      this.savedVisaDataUri = null;
    }

    // Immediately write to the hidden field if we're in saved mode
    if (this.currentMode === 'saved') {
      this.syncSavedVisa();
    }
  }

  syncSavedVisa() {
    if (!this.hasReviewDataTarget) return;
    if (this.savedVisaDataUri) {
      this.reviewDataTarget.value = this.savedVisaDataUri;
    }
  }

  blobToDataUri(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
}
